import { Calendar } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { button } from '~/ui-rocco/button-system';

export interface TeamMember {
    id: number;
    name: string;
    jobTitle: string;
    image: string;
}

export interface TeamGridProps {
    members: TeamMember[];
    className?: string;
    /** When set, tiles render as buttons (e.g. opening the member modal). */
    onMemberClick?: (member: TeamMember) => void;
    /** When set, each portrait carries the Book a meeting control. */
    onBookMeeting?: (member: TeamMember) => void;
}

/**
 * Book a meeting, resting on a team member's portrait.
 *
 * The member is offered by name because these sections only render people the
 * professional has made public, so the reader is choosing a face rather than
 * being handed a roster to pick from afterwards.
 */
export const TeamMemberBookMeetingButton = ({
    member,
    onBookMeeting,
}: {
    member: TeamMember;
    onBookMeeting: (member: TeamMember) => void;
}) => (
    <button
        type="button"
        onClick={() => onBookMeeting(member)}
        // Named here because the label is hidden on a phone.
        aria-label="Book a meeting"
        // Above the tile's own click target, which covers the whole tile —
        // this is the one place on it that does something else.
        className={button(
            'lifted',
            ['m', 'l'],
            'absolute bottom-4 left-4 z-10',
            {
                icon: 'max-md',
            },
        )}
    >
        {/* Plain calendar, not CalendarPlus: this leads to a booking for a
            member with a connected calendar and to a preferred-time request
            for everyone else, so the icon cannot promise a diary entry. */}
        <Calendar aria-hidden="true" className="size-[1.125rem] shrink-0" />
        <span className="max-md:hidden">Book a meeting</span>
    </button>
);

const TeamMemberTileBody = ({
    member,
    onBookMeeting,
}: {
    member: TeamMember;
    onBookMeeting?: (member: TeamMember) => void;
}) => (
    <>
        <div className="bg-neutral-200 relative aspect-[437/524] w-full overflow-hidden rounded-tile">
            {member.image ? (
                <img
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
            {onBookMeeting ? (
                <TeamMemberBookMeetingButton
                    member={member}
                    onBookMeeting={onBookMeeting}
                />
            ) : null}
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-[#181818] max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[1.375rem]">
            {member.name}
        </p>
        {member.jobTitle ? (
            <p className="m-0 font-sans leading-snug text-[#181818]/40 max-md:text-sm md:text-[1.375rem]">
                {member.jobTitle}
            </p>
        ) : null}
    </>
);

/**
 * Grid of team-member tiles (portrait photo + name + role) — the professional
 * "(02) Team" section. Two columns on mobile, four on desktop. Self-hides when
 * empty.
 */
export const TeamGrid = ({
    members,
    className,
    onMemberClick,
    onBookMeeting,
}: TeamGridProps) => {
    if (members.length === 0) return null;

    return (
        <div
            className={cn(
                'grid grid-cols-2 max-md:gap-x-2 max-md:gap-y-8 md:grid-cols-4 md:gap-x-3 md:gap-y-[2.4rem]',
                className,
            )}
        >
            {members.map((member) =>
                onMemberClick && onBookMeeting ? (
                    // Both offers on one tile. The tile cannot be the button
                    // then — Book a meeting is a button itself, and a button
                    // inside a button is not markup a browser will keep. The
                    // tile's own target becomes a layer over it instead, so
                    // the whole tile still opens the member and the one
                    // control that does something else sits above it.
                    <div key={member.id} className="relative flex flex-col">
                        <TeamMemberTileBody
                            member={member}
                            onBookMeeting={onBookMeeting}
                        />
                        <button
                            type="button"
                            onClick={() => onMemberClick(member)}
                            aria-label={member.name}
                            className="absolute inset-0 z-0 cursor-pointer"
                        />
                    </div>
                ) : onMemberClick ? (
                    <button
                        key={member.id}
                        type="button"
                        onClick={() => onMemberClick(member)}
                        className="flex cursor-pointer flex-col text-left"
                    >
                        <TeamMemberTileBody member={member} />
                    </button>
                ) : (
                    <div key={member.id} className="flex flex-col">
                        <TeamMemberTileBody
                            member={member}
                            onBookMeeting={onBookMeeting}
                        />
                    </div>
                ),
            )}
        </div>
    );
};
